import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";

import Page404 from "containers/Page404/Page404";
import Footer from "shared/Footer/Footer";
import ScrollToTop from "./ScrollToTop";

import FooterNav from "components/FooterNav";
import SiteHeader from "containers/SiteHeader";
import useWindowSize from "hooks/useWindowResize";

import { useAppDispatch } from "hooks/hooks";
import { useEffect } from "react";
import { Toaster } from "react-hot-toast";
import { currentUser } from "redux/features/auth/authSlice";
import {
  setFormData,
  setLoginCurrentStep,
  setVerificationMailCurrentStep,
} from "redux/features/signUp/signUpSlice";
import { useAuth } from "./auth";

import { pages, blogPages, privatePages } from "routers/routes";

const PublicRoute = ({ element }: any) => {
  const location = useLocation();
  const dispatch = useAppDispatch();

  useEffect(() => {
    // Remove 'blog' prefix from the domain if it exists
    const currentUrl = new URL(window.location.href);
    const { hostname, protocol, pathname, search, hash } = currentUrl;

    // Check if 'blog' is part of the hostname
    if (hostname.includes(".blog.")) {
      const cleanHostname = hostname.replace(".blog.", ".");
      const cleanUrl = `${protocol}//${cleanHostname}${pathname}${search}${hash}`;
      window.location.replace(cleanUrl); // Redirect to the clean URL
    }

    dispatch(currentUser());
  }, [location]);

  return element;
};

const BlogRoute = ({ targetPath, element }: any) => {
  const location = useLocation();

  useEffect(() => {
    // Get the current URL
    const currentUrl = new URL(window.location.href);

    // Construct the blog URL
    const blogUrl = (() => {
      const { protocol, hostname } = currentUrl;

      if (!hostname.includes("localhost")) {
        const parts = hostname.split(".");
        if (parts.length === 3) {
          // Example: dev.domain.com -> dev.blog.domain.com
          const [subdomain, ...rest] = parts;
          return `${protocol}//${subdomain}.blog.${rest.join(".")}`;
        } else if (parts.length === 2) {
          // Example: domain.com -> blog.domain.com
          return `${protocol}//blog.${hostname}`;
        }
      }

      return window.location.origin; // Fallback, should not be reached in typical cases
    })();

    // Extract dynamic segments from the targetPath and replace them with actual values
    const pathSegments = targetPath.match(/:([^/]+)/g) || [];
    const replacedPath = pathSegments.reduce(
      (path: string, segment: string) => path.replace(segment, location.pathname.split("/").pop() || ""),
      targetPath
    );

    // Construct the full new URL
    const newUrl = `${blogUrl}${replacedPath}${location.search}${location.hash}`;

    // Redirect if the current URL does not match
    if (window.location.href !== newUrl) {
      window.location.replace(newUrl);
    }
  }, [location, targetPath]);

  return element;
};

const PrivateRoute = ({ element, requiredRole, requiredType }: any) => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    // Remove 'blog' prefix from the domain if it exists
    const currentUrl = new URL(window.location.href);
    const { hostname, protocol, pathname, search, hash } = currentUrl;

    // Check if 'blog' is part of the hostname
    if (hostname.includes(".blog.")) {
      const cleanHostname = hostname.replace(".blog.", ".");
      const cleanUrl = `${protocol}//${cleanHostname}${pathname}${search}${hash}`;
      window.location.replace(cleanUrl); // Redirect to the clean URL
    }

    dispatch(currentUser());
  }, [location]);

  const { isAuthenticated, isEmailVirefied, isProfil, getUser, getUserRole, getUserType } = useAuth();

  const type = getUserType();

  if (
    isAuthenticated() &&
    getUserRole() === requiredRole &&
    (requiredType === undefined || requiredType.includes(type))
  ) {
    if (!isEmailVirefied() && getUserRole() !== "admin" && getUserRole() !== "creator") {
      dispatch(setVerificationMailCurrentStep());
      dispatch(
        setFormData({
          login: getUser()?.login,
          email: getUser()?.email,
          role: getUser()?.role,
        })
      );
      if (getUserRole() === "client") {
        return navigate("/signup/client");
      } else {
        return navigate("/signup/service");
      }
    } else if (!isProfil() && getUserRole() !== "admin" && getUserRole() !== "creator") {
      dispatch(setLoginCurrentStep());
      dispatch(
        setFormData({
          login: getUser()?.login,
          email: getUser()?.email,
          role: getUser()?.role,
        })
      );
      if (getUserRole() === "client") {
        return navigate("/signup/client");
      } else {
        return navigate("/signup/service");
      }
    } else {
      return element;
    }
  } else if (isAuthenticated() && getUserType() !== requiredType) {
    return <Navigate to="/" state={{ form: location }} />;
  }

  return <Navigate to="/login" state={{ form: location }} />;
};

const MyRoutes = () => {
  let WIN_WIDTH = useWindowSize().width;
  if (typeof window !== "undefined") {
    WIN_WIDTH = WIN_WIDTH || window.innerWidth;
  }

  return (
    <BrowserRouter>
      <Toaster position="top-right" reverseOrder={false} />

      <ScrollToTop />
      <SiteHeader />

      <Routes>
        {pages.map(({ component, path }) => {
          const Component = component;
          return <Route key={path} path={path} element={<PublicRoute element={<Component />} />} />;
        })}

        {blogPages.map(({ component, path }) => {
          const Component = component;
          // return (
          //   <Route key={path} path={path} element={<BlogRoute targetPath={path} element={<Component />} />} />
          // );
          return <Route key={path} path={path} element={<PublicRoute element={<Component />} />} />;
        })}

        {privatePages.map(({ component, path, requiredRole, requiredType }) => {
          const Component = component;
          return (
            <Route
              key={path}
              path={path}
              element={
                <PrivateRoute element={<Component />} requiredRole={requiredRole} requiredType={requiredType} />
              }
            />
          );
        })}

        <Route element={<Page404 />} />
      </Routes>

      {WIN_WIDTH < 768 && <FooterNav />}
      <Footer />
    </BrowserRouter>
  );
};

export default MyRoutes;
